import useAxios from "@/hooks/useAxios";
import { useResourceStore } from "@/stores/resourceStore"; // adjust if your store name differs
import { ApiModules } from "@/types/enums/api-modules";
import { Resource } from "@/types/resource"; // adjust path/name if needed
import { AxiosError } from "axios";
import { useState } from "react";
import { ApiErrorResponse } from "@/types/response";

/**
 * Resources API hooks
 *
 * Endpoints used (per your documentation):
 * - POST   /api/resources/         -> create resource (multipart/form-data)
 * - GET    /api/resources/         -> list resources
 * - GET    /api/resources/{id}/    -> get one resource
 * - PUT    /api/resources/{id}/    -> full update (multipart/form-data optional file)
 * - PATCH  /api/resources/{id}/    -> partial update (JSON)
 * - DELETE /api/resources/{id}/    -> delete resource
 */

/* ------------------------- GET ALL RESOURCES ------------------------- */
export const useGetAllResources = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);
  const { setResources } = useResourceStore();

  const getAllResources = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<Resource[]>("/resources/");
      if (data) {
        setResources(data);
        return { success: true, data };
      } else {
        throw new Error("Problem with fetching resources from server");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, getAllResources };
};

/* ------------------------- GET RESOURCE BY ID ------------------------- */
export const useGetResourceById = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);
  const { setSelectedResource } = useResourceStore();

  const getResourceById = async (resourceId?: number | string) => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const { data } = await axios.get<Resource>(`/resources/${resourceId}/`);
      if (data) {
        setSelectedResource(data.id!);
        return { success: true, data };
      } else {
        throw new Error("Unable to fetch resource");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, getResourceById };
};

/* ------------------------- CREATE RESOURCE -------------------------
   Expects multipart form data per docs:
   title, description, type, published_date, status and file (binary)
-------------------------------------------------------------------*/
export const useCreateResource = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);

  const createResource = async (payload: {
    title: string;
    description?: string;
    type?: string;
    published_date?: string; // YYYY-MM-DD
    status?: string; // 'draft' | 'published'
    file?: File; // optional but usually present
  }) => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append("title", payload.title);
      if (payload.description) form.append("description", payload.description);
      if (payload.type) form.append("type", payload.type);
      if (payload.published_date)
        form.append("published_date", payload.published_date);
      if (payload.status) form.append("status", payload.status);
      if (payload.file) form.append("file", payload.file);

      const { data } = await axios.post<Resource>("/resources/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data) {
        console.log("Resource created:", data);
        return { success: true, data: data as Resource };
      } else {
        throw new Error("Unable to create resource");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, createResource };
};

/* ------------------------- UPDATE RESOURCE (PUT) -------------------------
   Full update; `file` is optional (per docs).
-------------------------------------------------------------------*/
export const useUpdateResource = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);

  const updateResource = async (
    resourceId: number | string,
    payload: {
      title: string;
      description?: string;
      type?: string;
      published_date?: string;
      status?: string;
      file?: File | null;
    },
  ) => {
    if (!resourceId) return { success: false };
    setLoading(true);
    try {
      // Use FormData because file may be present
      const form = new FormData();
      form.append("title", payload.title);
      if (payload.description) form.append("description", payload.description);
      if (payload.type) form.append("type", payload.type);
      if (payload.published_date)
        form.append("published_date", payload.published_date);
      if (payload.status) form.append("status", payload.status);
      if (payload.file) form.append("file", payload.file);

      const { data } = await axios.put<Resource>(
        `/resources/${resourceId}/`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (data) {
        console.log("Resource updated:", data);
        return { success: true, data };
      } else {
        throw new Error("Unable to update resource");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateResource };
};

/* ------------------------- PATCH RESOURCE (partial update) ------------------------- */
export const usePatchResource = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);

  const patchResource = async (
    resourceId: number | string,
    partial: Partial<{
      title: string;
      description: string;
      type: string;
      published_date: string;
      status: string;
    }>,
  ) => {
    if (!resourceId) return { success: false };
    setLoading(true);
    try {
      const { data } = await axios.patch<any>(
        `/resources/${resourceId}/`,
        partial,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (data) {
        console.log("Resource patched:", data);
        return { success: true, data };
      } else {
        throw new Error("Unable to patch resource");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, patchResource };
};

/* ------------------------- DELETE RESOURCE ------------------------- */
export const useDeleteResource = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);

  const deleteResource = async (resourceId: number | string) => {
    if (!resourceId) return { success: false };
    setLoading(true);
    try {
      await axios.delete<any>(`/resources/${resourceId}/`);
      return { success: true };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, deleteResource };
};

/* ------------------------- UPLOAD MULTIPLE FILES / BULK -------------------------
   If you need a bulk upload endpoint (not in docs) you can call createResource multiple times.
   Below is a helper that uploads multiple files as separate resources and returns which failed.
-------------------------------------------------------------------*/
export const useBulkCreateResources = () => {
  const { axios } = useAxios(ApiModules.RESOURCES_PUBLICATION);

  const bulkCreateResources = async (
    items: Array<{
      title: string;
      file: File;
      description?: string;
      type?: string;
      published_date?: string;
      status?: string;
    }>,
  ) => {
    if (!items.length) return { success: false, failed: [] as string[] };

    const results = await Promise.allSettled(
      items.map((it) => {
        const form = new FormData();
        form.append("title", it.title);
        if (it.description) form.append("description", it.description);
        if (it.type) form.append("type", it.type);
        if (it.published_date) form.append("published_date", it.published_date);
        if (it.status) form.append("status", it.status);
        form.append("file", it.file);

        return axios.post("/resources/", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }),
    );

    const failed = results
      .map((r, i) => (r.status === "rejected" ? items[i].title : null))
      .filter(Boolean) as string[];

    return { success: failed.length === 0, failed };
  };

  return { bulkCreateResources };
};
